import { getPrimaryColor, Styles } from '@orcar/common-d2c';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { app } from '@/constants/common';

export const BLUE = getPrimaryColor(app);
export const RED = Styles.COLOR.RED;
export const GREEN = '#1FA97B';

export const Check = (
  <svg viewBox='0 0 24 24' fill='none' aria-hidden>
    <circle cx='12' cy='12' r='10' fill={BLUE} />
    <path
      d='M7 12.5l3 3 7-7'
      fill='none'
      stroke='#fff'
      strokeWidth='2.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

/** 부드럽게 열리는 아코디언 — 내용은 항상 DOM에 있어 SEO/스크린리더 OK */
export const Accordion = ({
  num,
  title,
  count,
  red,
  defaultOpen,
  children,
}: {
  num: number;
  title: string;
  count?: number;
  red?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <AccBox>
      <AccHead
        type='button'
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        $red={red}
      >
        <Num red={red}>{num}</Num>
        <AccTitle>{title}</AccTitle>
        {count != null && <Count $red={red}>{count}</Count>}
        <Chev $open={open} aria-hidden>
          ▾
        </Chev>
      </AccHead>
      <AccPanel $open={open}>
        <AccInner>
          <AccContent>{children}</AccContent>
        </AccInner>
      </AccPanel>
    </AccBox>
  );
};

/* ---------------- shared layout ---------------- */
export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Header = styled.div`
  border-radius: 16px;
  padding: 28px 24px;
  color: #fff;
  text-align: center;
  background: linear-gradient(135deg, ${BLUE}, ${getPrimaryColor(app)}cc);
`;

export const HeaderArt = styled.div`
  width: 60px;
  margin: 0 auto 10px;

  svg {
    width: 100%;
    height: auto;
    display: block;
  }
`;

export const HeaderTitle = styled.h2`
  ${Styles.FONT.H2};
  margin: 0;
  color: #fff;
`;

export const HeaderSub = styled.p`
  ${Styles.FONT.B1};
  margin: 8px 0 0;
  color: #fff;
  opacity: 0.95;
`;

export const Section = styled.section`
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 16px;
  padding: 22px;

  @media (max-width: 480px) {
    padding: 18px 16px;
  }
`;

export const SecHead = styled.h3<{ red?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  ${Styles.FONT.H3};
  color: ${({ red }) => (red ? RED : Styles.COLOR.GREY_01)};
`;

export const Num = styled.span<{ red?: boolean }>`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${({ red }) => (red ? RED : BLUE)};
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  border: 1px solid ${BLUE}33;
  border-radius: 14px;
  padding: 20px;
  background-color: ${BLUE}0a;
`;

export const CardHead = styled.div`
  display: inline-block;
  ${Styles.FONT.B1};
  font-weight: 800;
  color: #fff;
  background-color: ${BLUE};
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 14px;
`;

export const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_01};
  }

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
`;

export const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;

  li {
    ${Styles.FONT.B1};
    color: ${Styles.COLOR.GREY_01};
  }
`;

export const Note = styled.p`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  margin: 14px 0 0;
`;

export const InfoLine = styled.p`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_02};
  background-color: ${BLUE}0a;
  border-radius: 10px;
  padding: 12px 14px;
  margin: 16px 0 0;
`;

export const WarnBanner = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  background-color: ${RED}12;
  border: 1px solid ${RED}40;
  color: ${RED};
  ${Styles.FONT.B1};
  font-weight: 700;
  text-align: center;
`;

export const Chips = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

export const Chip = styled.div`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_01};
  background-color: ${RED}0a;
  border: 1px solid ${RED}26;
  border-radius: 10px;
  padding: 12px 14px;
`;

/* ---------------- accordion internals ---------------- */
const AccBox = styled.div`
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 16px;
  overflow: hidden;
`;

const AccHead = styled.button<{ $red?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  ${Styles.FONT.H3};
  color: ${({ $red }) => ($red ? RED : Styles.COLOR.GREY_01)};

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const AccTitle = styled.span``;

const Count = styled.span<{ $red?: boolean }>`
  ${Styles.FONT.B2};
  font-weight: 700;
  color: ${({ $red }) => ($red ? RED : BLUE)};
  background-color: ${({ $red }) => ($red ? `${RED}12` : `${BLUE}12`)};
  border-radius: 999px;
  padding: 2px 10px;
`;

const Chev = styled.span<{ $open: boolean }>`
  margin-left: auto;
  color: ${Styles.COLOR.GREY_03};
  transition: transform 0.25s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const AccPanel = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.28s ease;
`;

const AccInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const AccContent = styled.div`
  padding: 0 22px 22px;

  @media (max-width: 480px) {
    padding: 0 16px 18px;
  }
`;
